import React from 'react';

/**
 * Teamlio page header — one consistent look across the app.
 *
 * Visual DNA (defined in styles/global-theme.css):
 *   • `gt-eyebrow-row` — small caps label with a leaf glyph
 *   • `gt-page-title` — large display serif/sans mix, italics for the
 *     second word (or any <em> inside)
 *   • `gt-page-sub`   — quiet subtitle, italic accent on a key word
 *   • `gt-page-hero`  — flex row that lets `actions` sit on the right
 *
 * Example:
 *   <PageHeader
 *     eyebrow="II. Overview"
 *     title={<>Task <em>Management</em></>}
 *     subtitle={<>Manage and track all your <em>organizational</em> tasks.</>}
 *     actions={<button className="gt-btn is-primary">Create</button>}
 *   />
 */
interface PageHeaderProps {
  /** Small caps roman-numeral label — e.g. "I. Roster", "II. Overview". */
  eyebrow?: React.ReactNode;
  /** Main heading. Wrap a word in `<em>` to italicize it in serif. */
  title: React.ReactNode;
  /** Quiet description below the title. */
  subtitle?: React.ReactNode;
  /** Right-aligned actions (buttons, links). */
  actions?: React.ReactNode;
  /** Extra className on the outer wrapper. */
  className?: string;
}

export default function PageHeader({
  eyebrow,
  title,
  subtitle,
  actions,
  className = '',
}: PageHeaderProps) {
  return (
    <div className={`gt-page-hero flex-col md:flex-row gap-4 ${className}`}>
      <div className="min-w-0">
        {eyebrow && (
          <div className="gt-eyebrow-row">
            <span className="gt-leaf" />
            <span className="gt-eyebrow">{eyebrow}</span>
          </div>
        )}
        <h1 className="gt-page-title">{title}</h1>
        {subtitle && (
          <p className="gt-page-sub" style={{ marginTop: 6 }}>{subtitle}</p>
        )}
      </div>
      {actions && (
        <div className="flex items-center gap-2 flex-wrap">{actions}</div>
      )}
    </div>
  );
}
